/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import { LockSimple, Warning, type Icon } from "@phosphor-icons/react"
import type { ICellRendererParams } from "ag-grid-community"

import { StatusBadge } from "~/components/design-system/components"
import { Tooltip } from "~/components/reusable/Tooltip"
import { StatusColor } from "~/constants/status"
import { blockedByReason } from "~/features/feature-flags/client/model/labels"
import {
  companiesDescription,
  flagState,
  type CompanyStatus,
  type FlagState,
  type FlagStatus,
} from "~/features/feature-flags/client/model/status"
import { cn } from "~/utils/helpers"

const STATE_BADGE: Record<FlagState, { color: StatusColor; label: string }> = {
  on: { color: StatusColor.Success, label: "On" },
  off: { color: StatusColor.Neutral, label: "Off" },
  partial: { color: StatusColor.Warning, label: "Partial" },
}

const OFF_LIST_CUSTOM_REASON = "This custom flag wasn't built for this company."

/** A flag across companies, one company's setting, a value with the prerequisite keeping it off,
 * or a plain value. */
type BadgeStatus = FlagStatus | CompanyStatus | Pick<FlagStatus, "state" | "blockedBy"> | boolean

const isAcrossCompanies = (status: BadgeStatus): status is FlagStatus =>
  typeof status !== "boolean" && "companyCount" in status

const isCompanyStatus = (status: BadgeStatus): status is CompanyStatus =>
  typeof status !== "boolean" && "offList" in status

export type FlagStatusBadgeProps = {
  status: BadgeStatus
  /** Tooltip for a badge that isn't across companies. */
  description?: string | null
  className?: string
}

function Modifier({
  icon: IconComponent,
  title,
  className,
}: {
  icon: Icon
  title: string
  className: string
}) {
  return (
    <Tooltip title={title}>
      <span className="inline-flex shrink-0" aria-label={title} role="img">
        <IconComponent size={12} className={className} />
      </span>
    </Tooltip>
  )
}

/**
 * A flag's on / off / partial state as a `StatusBadge`, with a muted count for Partial and
 * icon modifiers for a blocking prerequisite and an off-list custom company.
 */
export function FlagStatusBadge({ status, description, className }: FlagStatusBadgeProps) {
  const { state, blockedBy } =
    typeof status === "boolean" ? { state: flagState(status), blockedBy: null } : status
  const { color, label } = STATE_BADGE[state]
  const across = isAcrossCompanies(status) ? status : null
  const offList = isCompanyStatus(status) && status.offList
  const tooltip = across ? companiesDescription(across) : description

  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <Tooltip title={tooltip ?? undefined}>
        <span className="inline-flex">
          <StatusBadge color={color} text={label} />
        </span>
      </Tooltip>
      {state === "partial" && across && (
        <span className="paragraph-sm text-fg-muted">
          {across.companiesOn} of {across.companyCount}
        </span>
      )}
      {blockedBy && (
        <Modifier icon={LockSimple} title={blockedByReason(blockedBy)} className="text-fg-muted" />
      )}
      {offList && (
        <Modifier icon={Warning} title={OFF_LIST_CUSTOM_REASON} className="text-fg-warning-icon" />
      )}
    </span>
  )
}

/** A grid cell showing the row's `status`. */
export function FlagStatusCell<Row extends { status: BadgeStatus }>({
  data,
}: ICellRendererParams<Row>) {
  return data ? <FlagStatusBadge status={data.status} /> : null
}
